/**
 * PWAInstallPrompt Component
 *
 * Displays a non-intrusive prompt to install the PWA
 * - Shows after 30 seconds on first visit
 * - Dismissable and remembers user preference
 * - Different UI for iOS (manual instructions) vs Chrome/Edge (native prompt)
 * - Automatically hides if app is already installed
 *
 * @module components/PWAInstallPrompt
 */

import { useState, useEffect } from 'react';
import { usePWA } from '../hooks/usePWA';

interface PWAInstallPromptProps {
  /**
   * Delay in milliseconds before showing the prompt
   * @default 30000 (30 seconds)
   */
  delay?: number;

  /**
   * Force show the prompt (for testing)
   * @default false
   */
  forceShow?: boolean;
}

/**
 * PWA Installation Prompt Component
 *
 * @example
 * // Default usage (30 second delay)
 * <PWAInstallPrompt />
 *
 * @example
 * // Custom delay (5 seconds)
 * <PWAInstallPrompt delay={5000} />
 *
 * @example
 * // Force show for testing
 * <PWAInstallPrompt forceShow />
 */
export function PWAInstallPrompt({ delay = 30000, forceShow = false }: PWAInstallPromptProps) {
  const {
    isInstallable,
    isInstalled,
    isIOS,
    isStandalone,
    promptDismissed,
    install,
    dismiss,
  } = usePWA();

  const [showPrompt, setShowPrompt] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [installing, setInstalling] = useState(false);

  // Show prompt after delay if conditions are met
  useEffect(() => {
    // Never show if already dismissed, installed, or in standalone mode
    if (promptDismissed || isInstalled || isStandalone) {
      return;
    }

    if (forceShow) {
      setShowPrompt(true);
      return;
    }

    // For iOS: only show if installable via Safari
    // For others: only show if browser supports install prompt
    if (isIOS || isInstallable) {
      const timer = setTimeout(() => {
        setShowPrompt(true);
      }, delay);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [delay, forceShow, promptDismissed, isInstalled, isStandalone, isIOS, isInstallable]);

  // Handle install button click
  const handleInstall = async () => {
    if (isIOS) {
      setShowIOSInstructions(true);
      return;
    }

    // IMPORTANT: Only attempt install if we have a deferred prompt
    // This prevents the "beforeinstallprompt.prompt() must be called" error
    if (!isInstallable) {
      if (import.meta.env.DEV) console.warn('[PWAInstallPrompt] Cannot install - no beforeinstallprompt event captured');
      setShowPrompt(false);
      return;
    }

    setInstalling(true);
    try {
      await install();
      setShowPrompt(false);
    } catch (err) {
      if (import.meta.env.DEV) console.error('[PWAInstallPrompt] Install failed:', err);
      // Don't show prompt again if install failed
      handleDismiss();
    } finally {
      setInstalling(false);
    }
  };

  // Handle dismiss
  const handleDismiss = () => {
    dismiss();
    setShowPrompt(false);
  };

  // Don't render if conditions aren't met
  if (!showPrompt) {
    return null;
  }

  // iOS Instructions Modal
  if (showIOSInstructions) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-md w-full p-6 animate-fadeIn">
          <div className="flex items-start justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Install Firebreak Bacchus
            </h3>
            <button
              onClick={() => setShowIOSInstructions(false)}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-4 text-sm text-gray-600 dark:text-gray-300">
            <p className="font-medium text-gray-900 dark:text-white">
              To install this app on your iPhone or iPad:
            </p>

            <ol className="space-y-3 ml-4 list-decimal">
              <li className="flex items-start gap-3">
                <span className="flex-1">
                  Tap the <strong>Share button</strong>
                  <svg className="inline-block w-4 h-4 mx-1" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z"/>
                  </svg>
                  in your Safari toolbar
                </span>
              </li>
              <li>
                Scroll down and tap <strong>&quot;Add to Home Screen&quot;</strong>
              </li>
              <li>
                Tap <strong>&quot;Add&quot;</strong> in the top right corner
              </li>
            </ol>

            <p className="text-xs text-gray-500 dark:text-gray-400 pt-2 border-t border-gray-200 dark:border-gray-700">
              The app icon will appear on your home screen like a native app.
            </p>
          </div>

          <div className="mt-6 flex gap-3">
            <button
              onClick={() => setShowIOSInstructions(false)}
              className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Close
            </button>
            <button
              onClick={handleDismiss}
              className="flex-1 px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
            >
              Don&apos;t Show Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Install Prompt Banner
  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 p-4 animate-slideUp">
      <div className="max-w-2xl mx-auto bg-white dark:bg-slate-800 rounded-lg shadow-2xl dark:border dark:border-slate-700 overflow-hidden">
        <div className="flex items-start gap-4 p-4">
          {/* App Icon */}
          <div className="flex-shrink-0">
            <img
              src="/icons/icon-96x96.png"
              alt="Firebreak Bacchus"
              className="w-12 h-12 rounded-lg shadow-sm"
            />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">
              Install Firebreak Bacchus
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
              {isIOS
                ? 'Get quick access to wildfire risk management from your home screen.'
                : 'Install this app for faster access and offline capabilities.'}
            </p>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={handleInstall}
                disabled={installing}
                className="px-4 py-2 text-sm font-medium text-white bg-gray-700 rounded-lg hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2 dark:focus:ring-offset-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {installing ? 'Installing...' : isIOS ? 'Show Instructions' : 'Install'}
              </button>
              <button
                onClick={handleDismiss}
                className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
              >
                Not Now
              </button>
            </div>
          </div>

          {/* Close button */}
          <button
            onClick={handleDismiss}
            className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

// Add animations to global CSS if not already present
const styles = `
@keyframes slideUp {
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.animate-slideUp {
  animation: slideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.animate-fadeIn {
  animation: fadeIn 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}
`;

// Inject styles (only once)
if (typeof document !== 'undefined') {
  const existingStyle = document.getElementById('pwa-install-prompt-styles');
  if (!existingStyle) {
    const styleSheet = document.createElement('style');
    styleSheet.id = 'pwa-install-prompt-styles';
    styleSheet.textContent = styles;
    document.head.appendChild(styleSheet);
  }
}
